import { ApiData, ApiDescription, RequestsMethod, Course } from '../common';

export const getUserCourses: ApiDescription = {
  endpoint: '/v3/courses',
  method: RequestsMethod.Get,
};
export type GetUserCourses = ApiData<never, Course[]>;

export const softDeleteCourse: ApiDescription = {
  endpoint: '/v3/courses/:id/soft-delete',
  method: RequestsMethod.Delete,
};

export const resetDeletedStatusForCourses: ApiDescription = {
  endpoint: '/v3/course/reset-deleted-status-for-courses',
  method: RequestsMethod.Patch,
};
export type ResetDeletedStatusForCourses = ApiData<unknown, any>;

export type SoftDeleteCourse = ApiData<unknown, any>;

export const getCourse: ApiDescription = {
  endpoint: '/courses/:id',
  method: RequestsMethod.Get,
};
export type GetCourse = ApiData<unknown, Course>;

export const getAllCourses: ApiDescription = {
  endpoint: '/v2/course/listAll',
  method: RequestsMethod.Get,
};
export type GetAllCourses = ApiData<unknown, Course[]>;

export const getAllCoursesLite: ApiDescription = {
  endpoint: '/v2/course/listAllTitles',
  method: RequestsMethod.Get,
};
export type GetAllCoursesLite = ApiData<unknown, Course[]>;

export const getCoursesByMentor: ApiDescription = {
  endpoint: '/v2/course/listByMentor',
  method: RequestsMethod.Get,
};
export type GetCoursesByMentor = ApiData<unknown, Course[]>;

export const uploadCourseImage: ApiDescription = {
  endpoint: '/v2/course/upload-image/:id',
  method: RequestsMethod.Post,
};

export type UploadCourseImage = ApiData<unknown, any>;

export const addCourse = {
  endpoint: '/v2/course/new',
  method: RequestsMethod.Post,
};

export type AddCourse = ApiData<unknown, unknown>;

export const editCourse = {
  endpoint: '/course/edit',
  method: RequestsMethod.Post,
};

export type EditCourse = ApiData<unknown, unknown>;

export const getDetails = {
  endpoint: '/course/get-details/:id',
  method: RequestsMethod.Get,
};

export type GetDetails = ApiData<unknown, unknown>;

export const listAvailableCourses = {
  endpoint: '/course/available',
  method: RequestsMethod.Get,
};

export type ListAvailableCourses = ApiData<unknown, unknown>;

export const listAll = {
  endpoint: '/v2/course/list-all',
  method: RequestsMethod.Get,
};

export type ListAll = ApiData<unknown, unknown>;

export type AdminCourseLmsStatus =
  | 'linked'
  | 'empty'
  | 'missing'
  | 'unlinked'
  | 'unavailable'
  | 'checking';

export type LmsUnitDTO = {
  id: string;
  title: string;
  type: string;
  duration?: number;
  isFree?: boolean;
};

export type LmsSectionDTO = {
  id: string;
  title: string;
  units: LmsUnitDTO[];
};

export type LmsContentSummaryDTO = {
  sectionCount: number;
  unitCount: number;
  unitsByType: Record<string, number>;
  isEmpty: boolean;
  fetchedAt: string;
};

export type LmsCatalogCourseDTO = {
  id: string;
  title: string;
  access?: string;
  price: number;
  createdAt?: string;
  modifiedAt?: string;
  content?: LmsContentSummaryDTO;
  contentError?: string;
  linkedCourseIds: string[];
};

export type LmsCourseContentDTO = LmsCatalogCourseDTO &
  LmsContentSummaryDTO & {
    sections: LmsSectionDTO[];
  };

export type AdminCourseEnrollmentStatsDTO = {
  active: number;
  total: number;
  mentors: number;
};

export type AdminCourseRowDTO = {
  _id: string;
  title: string;
  subtitle?: string;
  abbreviation?: string;
  color?: string;
  country?: string;
  isFree: boolean;
  isMentorless: boolean;
  isPublic: boolean;
  price?: number;
  lmsCourseId?: string;
  lmsCoursePrice?: number;
  hasImage: boolean;
  createdAt?: string;
  updatedAt?: string;
  legacy: { modules: number; lessons: number; quizzes: number };
  enrollments: AdminCourseEnrollmentStatsDTO;
  lms?: LmsCatalogCourseDTO;
  lmsStatus: AdminCourseLmsStatus;
};

export type AdminCoursesOverviewDTO = {
  courses: AdminCourseRowDTO[];
  lmsCatalog: LmsCatalogCourseDTO[];
  lmsFetchedAt?: string;
  lmsError?: string;
  lmsScan?: { total: number; done: number; startedAt: string };
};

export const getAdminCoursesOverview: ApiDescription = {
  endpoint: '/admin/courses/overview',
  method: RequestsMethod.Get,
};

export type GetAdminCoursesOverview = ApiData<{ refresh?: boolean }, AdminCoursesOverviewDTO>;

export type CourseMentorStudentDTO = {
  _id: string;
  fullName: string;
  graduationYear?: number;
  enrollmentId: string;
  enrollmentStatus?: number;
  isActive: boolean;
  sessionsDone: number;
  sessionsTotal: number;
};

export type CourseMentorDTO = {
  _id: string;
  fullName: string;
  status?: string;
  students: CourseMentorStudentDTO[];
};

export type AdminCourseInsightsDTO = {
  enrollments: AdminCourseEnrollmentStatsDTO;
  mentors: CourseMentorDTO[];
  lms: LmsCourseContentDTO | null;
  lmsStatus: AdminCourseLmsStatus;
  lmsError?: string;
};

export const getAdminCourseInsights: ApiDescription = {
  endpoint: '/admin/courses/:id/insights',
  method: RequestsMethod.Get,
};

export type GetAdminCourseInsights = ApiData<unknown, AdminCourseInsightsDTO>;

export type CourseEditableFields = {
  title: string;
  subtitle: string;
  abbreviation: string;
  lmsCourseId: string;
  lmsCoursePrice: number;
  isFree: boolean;
  isMentorless: boolean;
  isPublic: boolean;
  country: string;
  color: string;
  price: number;
  youtubeId: string;
  description: string;
  details: string;
};

export type AddCoursePayload = Pick<
  CourseEditableFields,
  | 'title'
  | 'subtitle'
  | 'abbreviation'
  | 'lmsCourseId'
  | 'lmsCoursePrice'
  | 'isFree'
  | 'isMentorless'
  | 'country'
  | 'color'
>;

export type AddCourseTyped = ApiData<AddCoursePayload, { message: string; id: string }>;

export type EditCourseTyped = ApiData<
  { id: string; changes: Partial<CourseEditableFields> },
  { message: string }
>;

export type CourseYearStatsDTO = {
  year: number;
  enrollments: number;
  students: number;
  active: number;
  completed: number;
  cancelled: number;
  suspended: number;
  sessionsHeld: number;
  averageSessions?: number;
  mentors: number;
  completionRate?: number;
};

export type CourseAssessmentDTO = {
  _id: string;
  title: string;
  abbreviation?: string;
  color?: string;
  isMentorless: boolean;
  isPublic: boolean;
  mentorsCanTeach: number;
  mentorsAvailable: number;
  mentorsTeachingNow: number;
  years: CourseYearStatsDTO[];
  allTime: Omit<CourseYearStatsDTO, 'year'>;
};

export type AdminCoursesAssessmentDTO = {
  years: number[];
  generatedAt: string;
  courses: CourseAssessmentDTO[];
};

export const getAdminCoursesAssessment: ApiDescription = {
  endpoint: '/admin/courses/assessment',
  method: RequestsMethod.Get,
};

export type GetAdminCoursesAssessment = ApiData<unknown, AdminCoursesAssessmentDTO>;
